import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

export interface UserRole {
  id: string;
  name: string;
  isSystem: boolean;
}

export interface TenantUser {
  id: string;
  email: string;
  name: string;
  userTenantId: string;
  legacyRole: string;
  roles: UserRole[];
  isDefault: boolean;
  joinedAt: string;
}

export function useUsers() {
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await apiFetch.get<{ users: TenantUser[] }>("/api/users");
        setUsers(data.users);
      } catch (err) {
        setError(err instanceof ApiError ? err : new Error("Error al cargar usuarios"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, [fetchKey]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  const assignRole = useCallback(
    async (userId: string, roleId: string): Promise<TenantUser> => {
      const result = await apiFetch.post<{ user: TenantUser }>(
        `/api/users/${userId}/roles`,
        { roleId }
      );
      setUsers((prev) => prev.map((u) => (u.id === userId ? result.user : u)));
      return result.user;
    },
    []
  );

  const removeRole = useCallback(
    async (userId: string, roleId: string): Promise<TenantUser> => {
      const result = await apiFetch.delete<{ user: TenantUser }>(
        `/api/users/${userId}/roles/${roleId}`
      );
      setUsers((prev) => prev.map((u) => (u.id === userId ? result.user : u)));
      return result.user;
    },
    []
  );

  return {
    users,
    isLoading,
    error,
    refetch,
    assignRole,
    removeRole,
  };
}

export function useUser(userId: string | undefined) {
  const [user, setUser] = useState<TenantUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    const fetchUser = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await apiFetch.get<{ user: TenantUser }>(`/api/users/${userId}`);
        setUser(data.user);
      } catch (err) {
        setError(err instanceof ApiError ? err : new Error("Error al cargar usuario"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  const assignRole = useCallback(
    async (roleId: string): Promise<void> => {
      if (!userId) return;
      const result = await apiFetch.post<{ user: TenantUser }>(
        `/api/users/${userId}/roles`,
        { roleId }
      );
      setUser(result.user);
    },
    [userId]
  );

  const removeRole = useCallback(
    async (roleId: string): Promise<void> => {
      if (!userId) return;
      const result = await apiFetch.delete<{ user: TenantUser }>(
        `/api/users/${userId}/roles/${roleId}`
      );
      setUser(result.user);
    },
    [userId]
  );

  return { user, isLoading, error, assignRole, removeRole };
}
